import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PROXY_API_PATH } from '../constants';

const PageContentContext = createContext(null);

const CONTENT_KEY = 'hm_page_content';
const EDIT_MODE_KEY = 'hm_edit_mode';
const ADMIN_TOKEN_KEY = 'hm_admin_token';

function readContentFromStorage() {
    try {
        const raw = localStorage.getItem(CONTENT_KEY);
        if (raw) return JSON.parse(raw);
    } catch {}
    return {};
}

// Resolve admin status from localStorage (token presence) — no React deps so
// it can be called from initial state.
const checkIsAdmin = () => {
    try {
        return !!localStorage.getItem(ADMIN_TOKEN_KEY) ||
               sessionStorage.getItem('hm_admin_token_auth') === 'true';
    } catch { return false; }
};

export function PageContentProvider({ children }) {
    const [content, setContent] = useState(readContentFromStorage);
    // Defence in depth: only honour the persisted edit-mode flag if the
    // current viewer is actually an admin. Otherwise an old cached `true`
    // would bleed edit affordances into a non-admin browser.
    const [isEditMode, setIsEditMode] = useState(() => {
        try {
            return checkIsAdmin() && localStorage.getItem(EDIT_MODE_KEY) === 'true';
        } catch { return false; }
    });
    const [saving, setSaving] = useState(false);
    const saveTimer = useRef(null);
    const pendingContent = useRef(null);

    // Re-read when App.js fires hm-settings-loaded (startup fetch completes)
    useEffect(() => {
        const onRefresh = () => setContent(readContentFromStorage());
        window.addEventListener('hm-settings-loaded', onRefresh);
        return () => window.removeEventListener('hm-settings-loaded', onRefresh);
    }, []);

    // Reset edit mode the moment the admin signs out. The AuthContext already
    // dispatches `hm-admin-signout` on signOut() — we listen for it here.
    useEffect(() => {
        const onSignOut = () => {
            try { localStorage.removeItem(EDIT_MODE_KEY); } catch {}
            setIsEditMode(false);
        };
        window.addEventListener('hm-admin-signout', onSignOut);
        return () => window.removeEventListener('hm-admin-signout', onSignOut);
    }, []);

    const isAdmin = useCallback(() => checkIsAdmin(), []);

    const toggleEditMode = useCallback(() => {
        // Guard: non-admins can never enable edit mode, even programmatically.
        if (!checkIsAdmin()) {
            try { localStorage.removeItem(EDIT_MODE_KEY); } catch {}
            setIsEditMode(false);
            return;
        }
        setIsEditMode(prev => {
            const next = !prev;
            try { localStorage.setItem(EDIT_MODE_KEY, String(next)); } catch {}
            return next;
        });
    }, []);

    const getContent = useCallback((key, defaultValue = '') => {
        return content[key] !== undefined ? content[key] : defaultValue;
    }, [content]);

    const saveContent = useCallback((key, value) => {
        // Optimistic local update immediately
        setContent(prev => {
            const next = { ...prev, [key]: value };
            try { localStorage.setItem(CONTENT_KEY, JSON.stringify(next)); } catch {}
            pendingContent.current = next;
            return next;
        });

        // Debounced server persist (600ms)
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            if (!token || !pendingContent.current) return;
            setSaving(true);
            try {
                await fetch(`${PROXY_API_PATH}/admin/site-settings`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
                    body: JSON.stringify({ key: CONTENT_KEY, value: pendingContent.current }),
                });
            } catch {}
            setSaving(false);
        }, 600);
    }, []);

    // resetPage — removes all keys whose prefix matches, restoring fallback defaultValues in components
    const resetPage = useCallback(async (prefixes) => {
        const prefixList = Array.isArray(prefixes) ? prefixes : [prefixes];
        const token = localStorage.getItem(ADMIN_TOKEN_KEY);
        if (!token) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        setSaving(true);

        // Read current content directly from localStorage (source of truth)
        const current = readContentFromStorage();
        const newContent = Object.fromEntries(
            Object.entries(current).filter(
                ([k]) => !prefixList.some(p => k === p || k.startsWith(p + '.'))
            )
        );

        // Update localStorage and React state immediately
        try { localStorage.setItem(CONTENT_KEY, JSON.stringify(newContent)); } catch {}
        setContent(newContent);
        pendingContent.current = newContent;

        // Server flush — immediate, no debounce (explicit admin action)
        try {
            await fetch(`${PROXY_API_PATH}/admin/site-settings`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-token': token },
                body: JSON.stringify({ key: CONTENT_KEY, value: newContent }),
            });
        } catch {}
        setSaving(false);
    }, []);
    // Memoised: App re-renders on every navigation, and a fresh object here
    // re-rendered every consumer (memoised cards included) each time.
    const pageContentValue = useMemo(() => ({ isEditMode, toggleEditMode, getContent, saveContent, resetPage, saving, isAdmin }),
        [isEditMode, toggleEditMode, getContent, saveContent, resetPage, saving, isAdmin]);

    return (
        <PageContentContext.Provider value={pageContentValue}>
            {children}
        </PageContentContext.Provider>
    );
}

export function usePageContent() {
    return useContext(PageContentContext);
}
